import type { DataLayerEntry, TrackingEvents } from '@platform/tracking';

/**
 * A GA4 event object: an `event` name plus arbitrary BI-contract params, so a
 * mapper can emit extra fields or arrays, not just `event_description`/`event_details`.
 */
export interface Ga4Event {
  event: string;
  [key: string]: unknown;
}

type EventMapper<E extends keyof TrackingEvents = keyof TrackingEvents> = (params: TrackingEvents[E]) => Ga4Event | null;

export type EventMappers = { [E in keyof TrackingEvents]?: EventMapper<E> };

type UntypedMapper = (params: Record<string, unknown>) => Ga4Event | null;

const registry = new Map<string, UntypedMapper>();

export function registerGa4Events(events: EventMappers) {
  for (const [name, mapper] of Object.entries(events)) {
    if (mapper) {
      registry.set(name, mapper as UntypedMapper);
    }
  }
}

/** Event names that have a registered mapper — the tracking inspector's coverage catalog. */
export function getRegisteredEventNames(): string[] {
  return [...registry.keys()];
}

/** @internal — test-only reset */
export function _resetRegistry() {
  registry.clear();
}

function buildBaseFields(entry: DataLayerEntry): Record<string, unknown> {
  return {
    app_version: entry.config.release,
    device_platform: entry.config.platform,
    user_id: entry.userId,
    session_id: entry.sessionId,
    client_id: entry.gaClientId,
    timestamp: Math.floor(entry.timestamp / 1000),
    user_agent: navigator.userAgent,
    screen_name: entry.meta.page,
  };
}

/**
 * GA4 delivery is opt-in per event: without a mapper the event reaches /collect only. A mapper is what
 * turns an internal event into the projection GTM reads, so it is also the place to decide there is one.
 */
export function hubDataLayerAdapter(entry: DataLayerEntry): Record<string, unknown> | null {
  const ga4Event = registry.get(entry.name)?.(entry.params);
  if (!ga4Event) {
    return null;
  }

  return { ...ga4Event, ...buildBaseFields(entry) };
}
